import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useAuthStore } from './store';
import AdminLayout from './components/AdminLayout';
import LoadingSpinner from './components/LoadingSpinner';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Lazy load pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Orders = lazy(() => import('./pages/Orders'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const Products = lazy(() => import('./pages/Products'));
const ProductEdit = lazy(() => import('./pages/ProductEdit'));
const Users = lazy(() => import('./pages/Users'));
const Resellers = lazy(() => import('./pages/Resellers'));
const EndUsers = lazy(() => import('./pages/EndUsers'));
const Employees = lazy(() => import('./pages/Employees'));
const Sellers = lazy(() => import('./pages/Sellers'));
const Resources = lazy(() => import('./pages/Resources'));
const Materials = lazy(() => import('./pages/Materials'));
const PropModels = lazy(() => import('./pages/PropModels'));
const Settings = lazy(() => import('./pages/Settings'));
const Scene = lazy(() => import('./pages/Scene'));
const ShoppingCarts = lazy(() => import('./pages/ShoppingCarts'));
const SalesOffers = lazy(() => import('./pages/SalesOffers'));
const SalesOfferEdit = lazy(() => import('./pages/SalesOfferEdit'));
const SalesOrders = lazy(() => import('./pages/SalesOrders'));
const SalesOrderEdit = lazy(() => import('./pages/SalesOrderEdit'));
const ProductAssemblyDesigner = lazy(() => import('./pages/ProductAssemblyDesigner'));
const GroupsPage = lazy(() => import('./pages/GroupsPage'));
const Sandbox = lazy(() => import('./pages/Sandbox'));
const SandboxEditor = lazy(() => import('./pages/SandboxEditor'));
const Visitors = lazy(() => import('./pages/Visitors'));
const WebsiteContentList = lazy(() => import('./pages/WebsiteContentList'));
const WebsiteContentEdit = lazy(() => import('./pages/WebsiteContentEdit'));
const WebsiteVideos = lazy(() => import('./pages/WebsiteVideos'));

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/visitors" element={<Visitors />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/orders/:id" element={<OrderDetail />} />
                  <Route path="/shopping-carts" element={<ShoppingCarts />} />
                  <Route path="/sales-offers" element={<SalesOffers />} />
                  <Route path="/sales-offers/:id" element={<SalesOfferEdit />} />
                  <Route path="/sales-orders" element={<SalesOrders />} />
                  <Route path="/sales-orders/:id" element={<SalesOrderEdit />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:id" element={<ProductEdit />} />
                  <Route path="/products/:id/assembly" element={<ProductAssemblyDesigner />} />
                  <Route path="/groups" element={<GroupsPage />} />
                  <Route path="/users" element={<Users />} />
                  <Route path="/resellers" element={<Resellers />} />
                  <Route path="/end-users" element={<EndUsers />} />
                  <Route path="/employees" element={<Employees />} />
                  <Route path="/sellers" element={<Sellers />} />
                  <Route path="/resources" element={<Resources />} />
                  <Route path="/materials" element={<Materials />} />
                  <Route path="/prop-models" element={<PropModels />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/scene" element={<Scene />} />
                  <Route path="/scene/:code" element={<Scene />} />
                  <Route path="/sandbox" element={<Sandbox />} />
                  <Route path="/sandbox/:id" element={<SandboxEditor />} />
                  <Route path="/website/posts" element={<WebsiteContentList kind="post" />} />
                  <Route path="/website/posts/:id" element={<WebsiteContentEdit />} />
                  <Route path="/website/pages" element={<WebsiteContentList kind="page" />} />
                  <Route path="/website/pages/:id" element={<WebsiteContentEdit />} />
                  <Route path="/website/videos" element={<WebsiteVideos />} />
                  {/* Redirect old order-backlog URLs to sales-offers */}
                  <Route path="/order-backlog" element={<Navigate to="/sales-offers" replace />} />
                  <Route path="/order-backlog/:id" element={<Navigate to="/sales-offers" replace />} />
                </Routes>
              </AdminLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}
